import React, { useState } from "react";
import { Plus, Pencil, Trash2, Package } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PrimaryButton, GhostButton, EmptyState, Field, inputCls, formatCOP } from "@/components/common";
import { DocTable, IconBtn } from "./shared";

const empty = { name: "", description: "", unit_price: 0, unit: "mes", category: "" };
const UNITS = [["mes", "Por mes"], ["unidad", "Por unidad"], ["hora", "Por hora"], ["proyecto", "Por proyecto"]];

export default function ServiciosTab({ services, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (s) => { setEditing(s.service_id); setForm({ ...empty, ...s }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, unit_price: Number(form.unit_price) || 0 };
    try {
      if (editing) await api.put(`/services/${editing}`, payload);
      else await api.post("/services", payload);
      toast.success(editing ? "Servicio actualizado" : "Servicio creado");
      setOpen(false); reload();
    } catch { toast.error("No se pudo guardar el servicio"); }
  };

  const remove = async (s) => {
    if (!window.confirm(`¿Eliminar el servicio "${s.name}"?`)) return;
    await api.delete(`/services/${s.service_id}`);
    toast.success("Servicio eliminado"); reload();
  };

  return (
    <div data-testid="servicios-tab">
      <div className="flex justify-between items-center mb-4 gap-3 flex-wrap">
        <p className="text-sm text-[#9CA3AF]">Tu catálogo de servicios con precios. Úsalos para armar cotizaciones rápidamente.</p>
        <PrimaryButton data-testid="add-service-btn" onClick={openNew}><Plus className="w-4 h-4"/> Nuevo servicio</PrimaryButton>
      </div>
      {services.length === 0 ? (
        <EmptyState icon={Package} title="Aún no tienes servicios" description="Crea los servicios que ofreces (manejo de redes, pauta, videos, diseño…) con su precio base."
          action={<PrimaryButton onClick={openNew}><Plus className="w-4 h-4"/> Crear servicio</PrimaryButton>}/>
      ) : (
        <DocTable headers={["Servicio", "Categoría", "Unidad", { label: "Precio", right: true }, { label: "Acciones", right: true }]}>
          {services.map(s => (
            <tr key={s.service_id} data-testid={`service-row-${s.service_id}`} className="border-b border-[#262A33] last:border-0 hover:bg-[#1A1D24] transition-colors">
              <td className="px-5 py-3">
                <div className="text-[#F5F5F7] font-medium">{s.name}</div>
                {s.description && <div className="text-xs text-[#6B7280] mt-0.5 line-clamp-1">{s.description}</div>}
              </td>
              <td className="px-5 py-3 text-[#9CA3AF]">{s.category || "—"}</td>
              <td className="px-5 py-3 text-[#9CA3AF] capitalize">{s.unit}</td>
              <td className="px-5 py-3 text-right text-[#F5F5F7] font-medium">{formatCOP(s.unit_price)}</td>
              <td className="px-5 py-3 text-right">
                <div className="inline-flex gap-1">
                  <IconBtn data-testid={`edit-service-${s.service_id}`} onClick={() => openEdit(s)} className="text-[#9CA3AF] hover:text-[#F5F5F7] hover:bg-[#262A33]" aria-label="Editar"><Pencil className="w-4 h-4"/></IconBtn>
                  <IconBtn data-testid={`delete-service-${s.service_id}`} onClick={() => remove(s)} className="text-[#6B7280] hover:text-red-400 hover:bg-red-500/10" aria-label="Eliminar"><Trash2 className="w-4 h-4"/></IconBtn>
                </div>
              </td>
            </tr>
          ))}
        </DocTable>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#15171E] border-[#262A33] text-[#F5F5F7]">
          <DialogHeader><DialogTitle style={{ fontFamily: "Manrope, sans-serif" }}>{editing ? "Editar servicio" : "Nuevo servicio"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <Field label="Nombre del servicio *">
              <input data-testid="service-name-input" required className={inputCls} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Ej. Manejo de redes sociales"/>
            </Field>
            <Field label="Descripción" hint="Aparece debajo del nombre en la cotización.">
              <textarea data-testid="service-desc-input" rows={2} className={inputCls} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Ej. Instagram, Facebook y TikTok · 12 videos al mes"/>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Precio base (COP) *">
                <input data-testid="service-price-input" required type="number" min="0" className={inputCls} value={form.unit_price} onChange={e => setForm({ ...form, unit_price: e.target.value })}/>
              </Field>
              <Field label="Unidad">
                <select className={inputCls} value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })}>
                  {UNITS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Categoría">
              <input className={inputCls} value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} placeholder="Ej. Redes, Pauta, Producción"/>
            </Field>
            <DialogFooter>
              <GhostButton type="button" onClick={() => setOpen(false)}>Cancelar</GhostButton>
              <PrimaryButton data-testid="save-service-btn" type="submit">{editing ? "Guardar" : "Crear servicio"}</PrimaryButton>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
